import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Inbox, MessagesSquare, Settings, CreditCard,
  LogOut, Building2, Layers, PenSquare, Mail, ArrowLeft, Waypoints, Users, Megaphone, UserCog, FolderOpen,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const NAVS = {
  business: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
    { to: "/dashboard/forms", label: "Forms", icon: FileText, testid: "nav-forms" },
    { to: "/dashboard/documents", label: "Documents", icon: FolderOpen, testid: "nav-documents" },
    { to: "/dashboard/submissions", label: "Submissions", icon: Inbox, testid: "nav-submissions" },
    { to: "/dashboard/conversations", label: "Conversations", icon: MessagesSquare, testid: "nav-conversations" },
    { to: "/dashboard/customers", label: "Customers", icon: Users, testid: "nav-customers" },
    { to: "/dashboard/broadcasts", label: "Broadcasts", icon: Megaphone, testid: "nav-broadcasts" },
    { to: "/dashboard/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
    { to: "/dashboard/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
    { to: "/dashboard/account", label: "Account", icon: UserCog, testid: "nav-account" },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, testid: "nav-admin-overview" },
    { to: "/admin/businesses", label: "Businesses", icon: Building2, testid: "nav-admin-businesses" },
    { to: "/admin/templates", label: "Templates", icon: LayoutDashboard, testid: "nav-admin-templates" },
    { to: "/admin/plans", label: "Plans", icon: Layers, testid: "nav-admin-plans" },
    { to: "/admin/cms", label: "Homepage CMS", icon: PenSquare, testid: "nav-admin-cms" },
    { to: "/admin/settings", label: "Settings", icon: Settings, testid: "nav-admin-settings" },
    { to: "/admin/emails", label: "Email Log", icon: Mail, testid: "nav-admin-emails" },
    { to: "/admin/account", label: "Account", icon: UserCog, testid: "nav-admin-account" },
  ],
};

export function DashboardLayout({ kind, children }) {
  const { user, logout, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const impersonating = !!localStorage.getItem("impersonate_token");
  const items = NAVS[kind].filter(
    (i) => !(user?.staff_role === "agent" && ["/dashboard/billing", "/dashboard/settings"].includes(i.to))
  );

  const isActive = (to) =>
    to === `/${kind === "admin" ? "admin" : "dashboard"}`
      ? location.pathname === to
      : location.pathname.startsWith(to);

  const exitImpersonation = async () => {
    localStorage.removeItem("impersonate_token");
    await refresh();
    navigate("/admin/businesses");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid={`${kind}-layout`}>
      <aside className="w-64 shrink-0 border-r border-[#E5E5E5] bg-white flex flex-col fixed inset-y-0 z-20">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-[#E5E5E5]">
          <Waypoints className="h-5 w-5" />
          <span className="font-heading font-bold tracking-tight">CreateForms.app</span>
          {kind === "admin" && (
            <span className="ml-auto text-[10px] uppercase tracking-widest bg-neutral-900 text-white px-2 py-0.5 rounded-full">Admin</span>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                isActive(item.to)
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E5E5E5]">
          <div className="px-3 py-2">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-neutral-500 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          </div>
          <Button variant="ghost" className="w-full justify-start rounded-lg text-neutral-600" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        {impersonating && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center justify-between" data-testid="impersonation-banner">
            <span className="text-sm text-amber-900">
              You are viewing this account as <b>{user?.business?.name || "business"}</b>
            </span>
            <Button size="sm" variant="outline" className="rounded-full border-amber-300" onClick={exitImpersonation} data-testid="exit-impersonation-button">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Return to admin
            </Button>
          </div>
        )}
        <main className="flex-1 p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
